import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { UserState, fetchUserArg } from "../../assets/interfaces/interfaces";
import { fetchUpdateFirestore } from "./updateFieldSlice";
import { getAuth } from "firebase/auth";
import { findData } from "../../components/auth/AuthListener";
import { parsePhoneNumber } from "libphonenumber-js";
import { injectCjConversion, injectTrackingJS } from "../../components/Session/helpers";
import { doSignOutClear } from "../../components/auth/helpers";
import Cookies from "universal-cookie";
import { currentVersion } from "../../components/terms_and_policies/TermsAndPolicies";
import { RootState } from "../store";
import { showPromo } from "./IntroPromoSlice";

const initialState: UserState = {
  user: {
    accessToken: "",
    userId: "",
    phoneNumber: "",
    feature_flags: "",
    country: "",
    state: "",
    email: "",
    display_name: "",
    name: "",
    ip: "",
    sign_in_attempts: "",
    photoURL: "",
  }, // Initialized as an empty array
  status: "idle",
  error: null,
  showToSModal: false,
  isManualSignUp: false,
  isNewUser: false,
  isAcknowledged: false,
  authModal: {
    isOpen: false,
    type: null,
    disableClose: false,
  },
  notifications: null,
};

export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (arg: fetchUserArg, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const { source } = arg;
      const auth = getAuth();
      const user = auth?.currentUser || "";
      const { data, status } = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/user`,
        { withCredentials: true },
      );
      if (user && status === 200 && !data?.user?.tradegpt) {
        dispatch(setIsNewUser());
        const email = user?.email || "";
        let phone
          = localStorage.getItem("phoneNumber") || user.phoneNumber || "";
        let name = user?.displayName || "";
        const userId = user?.uid || "";
        const cookies = new Cookies();
        const cjeCookie = cookies.get("cje");
        let country = "";
        const { phoneNumber, displayName } = findData(user);
        if (displayName?.length > name?.length) {
          name = displayName;
        }
        if (phone?.length < phoneNumber?.length) {
          phone = phoneNumber;
        }
        if (phone.length) {
          country = parsePhoneNumber(phone)?.country || "";
        }
        const firestoreFields = {
          email,
          tradegpt: {
            phoneNumber: phone,
            displayName,
            country,
            ...(state.user?.isManualSignUp && {
              signed_agreement: {
                [currentVersion]: Date.now(), // Only add auto acknowledgement if ManualSignUp is true
              },
            }),
          },
        };
        // inject cje script
        injectCjConversion("464052", 0, userId, email, cjeCookie, userId);
        injectTrackingJS(1);
        dispatch(fetchUpdateFirestore({ firestoreFields, source }));
        if (state.user?.isManualSignUp) {
          dispatch(setIsAcknowledged(true));
          dispatch(showPromo());
        }
      }
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          // Lets log the user out if token is expired
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const updateNotifPreferences = createAsyncThunk(
  "tasks/updateTask",
  async (arg: { account: boolean; marketing: boolean }) => {
    try {
      const updatedNotifs = {
        email: {
          ...arg,
        },
      };
      await axios.put(`${process.env.REACT_APP_SPL_URL}/v1/user/tradegpt-settings`, {
        notifications: updatedNotifs,
      }, { withCredentials: true });

      return updatedNotifs;
    }
    catch (error) {
      console.error("Error renaming chat:", error);
      throw error;
    }
  },
);

export const activeUserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setShowToSModal: (state, action) => {
      state.showToSModal = action.payload;
    },
    setIsManualSignUp: (state) => {
      state.isManualSignUp = true;
    },
    setIsNewUser: (state) => {
      state.isNewUser = true;
    },
    setIsAcknowledged: (state, action) => {
      state.isAcknowledged = action.payload;
    },
    openAuthModal: (state, action: PayloadAction<{ type: "signin" | "signup"; disableClose?: boolean }>) => {
      state.authModal.isOpen = true;
      state.authModal.type = action.payload.type;
      state.authModal.disableClose = action.payload.disableClose ?? false;
    },
    closeAuthModal: (state) => {
      state.authModal.isOpen = false;
      state.authModal.type = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload.user;
        state.notifications = action.payload.user.tradegpt.notifications;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch active plan";
      })
      .addCase(updateNotifPreferences.fulfilled, (state, action) => {
        state.notifications = action.payload;
      });
  },
});

export const { setShowToSModal, setIsManualSignUp, setIsNewUser, setIsAcknowledged, openAuthModal, closeAuthModal } = activeUserSlice.actions;
export default activeUserSlice.reducer;
